/**
 * Page state that survives Back and Forward: kept in the URL, or — where it names a
 * person — in the browser's history entry.
 *
 * A page's own settings — the Overview's selected bar, whose team is open, a table's
 * sort — used to be component state, gone the moment you left the page. So "← Back"
 * from a drill landed on the right page but not where you were (Asana: "Add Back
 * button for navigation", 2026-09-24).
 *
 * Two homes, one hook:
 *
 *  - The URL's query string, for settings that name nobody: a dimension, a department,
 *    a facility, a sort. Back and Forward restore them, and a copied link opens the
 *    same view.
 *  - The history entry's state, for anything that names a person — whose team, whose
 *    drill-through, a picked employee. Back and Forward restore these exactly as well,
 *    but they never reach the address bar. A URL carrying staff names ends up in
 *    browser history, proxy logs and pasted links (the reason claimDrill.ts already
 *    moves its selection in router state), and this app is individual pay.
 *
 * Either way:
 *  - Changes REPLACE the current history entry rather than adding one, so Back leaves
 *    the page instead of stepping through every click made on it.
 *  - A value at its default is left out, so an untouched page stays clean — and the
 *    tabs, which link to bare paths with no state, still open every page fresh.
 *  - Each write reads the live URL and history entry, not the ones this render saw.
 *    React Router's own setSearchParams does the latter, so three settings changed in
 *    one click (the Overview does this) kept only the last. Each write also carries the
 *    other home's contents forward, so a URL write never drops router state (a drill
 *    handoff) and a personal write never drops the query string.
 *
 * A copied link (state/sharedView.ts, docs/STATE.md) carries both homes: the query
 * string as it is, and the personal values with every name swapped for an opaque
 * person key. The receiver's browser swaps keys back for the people it is served. A
 * personal value that is not portable as it stands, or must never leave the tab, says
 * so with `share` below. The filter bar lives in the same two homes (data/filters.tsx).
 */
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { INCOMING_KEY, VIEW_KEY, type Sharer } from '@/state/sharedView'

export interface Codec<T> {
  parse: (raw: string) => T
  /** Null means "leave it out". */
  format: (value: T) => string | null
}

export const text: Codec<string> = { parse: (s) => s, format: (v) => (v === '' ? null : v) }

export const maybeText: Codec<string | null> = {
  parse: (s) => s,
  format: (v) => (v === null || v === '' ? null : v),
}

export const int: Codec<number> = {
  parse: (s) => (Number.isFinite(Number(s)) ? Math.trunc(Number(s)) : 0),
  format: (v) => String(v),
}

export const bool: Codec<boolean> = { parse: (s) => s === '1', format: (v) => (v ? '1' : null) }

/**
 * A list of names. Joined on "|", not ",": people are written "Last, First", so a
 * comma would split every one of them in two.
 */
export const list: Codec<string[]> = {
  parse: (s) => s.split('|').filter(Boolean),
  format: (v) => (v.length ? v.join('|') : null),
}

/** Any JSON value — for a set of dropdowns, or a handed-over team. Unreadable reads as `fallback`. */
export const json = <T,>(fallback: T): Codec<T> => ({
  parse: (s) => {
    try {
      return JSON.parse(s) as T
    } catch {
      return fallback
    }
  },
  format: (v) => JSON.stringify(v),
})

/**
 * A table's sort, written `key.dir` (`?sort=cpc.desc`), as DataTable's `sortParam`
 * writes it; null for unsorted. A column key may itself hold dots, so the direction
 * is whatever follows the last one.
 */
export const sortSpec: Codec<{ key: string; dir: 'asc' | 'desc' } | null> = {
  parse: (s) => {
    const at = s.lastIndexOf('.')
    const key = at > 0 ? s.slice(0, at) : ''
    return key ? { key, dir: s.slice(at + 1) === 'asc' ? 'asc' : 'desc' } : null
  },
  format: (v) => (v ? `${v.key}.${v.dir}` : null),
}

/** One of a fixed set; anything else reads as the fallback. */
export const oneOf = <T extends string>(values: readonly T[], fallback: T): Codec<T> => ({
  parse: (s) => ((values as readonly string[]).includes(s) ? (s as T) : fallback),
  format: (v) => v,
})

/** Where personal view state sits inside router state. */
const VIEW = VIEW_KEY

type RouterState = Record<string, unknown> & { [VIEW]?: Record<string, string> }

/** Router state as the browser holds it right now — React Router keeps it under `usr`. */
export const liveState = (): RouterState => {
  const usr = (window.history.state as { usr?: unknown } | null)?.usr
  return usr && typeof usr === 'object' ? { ...(usr as RouterState) } : {}
}

/**
 * How a personal value goes into a shared link (state/sharedView.ts). By default every
 * name in it becomes an opaque key, which is right for names, lists of names and JSON
 * holding names. Two cases need the page to say more:
 *
 *  - `false`: never shared. Free text typed into a search box can hold anything.
 *  - an adapter: the value is not portable as it stands — dropdown indexes into a
 *    dictionary the API builds per caller. `out` turns it into values; `in` turns
 *    values back into this caller's indexes, and returns undefined until it can (the
 *    rows have not loaded). Null from either means "leave it out".
 */
export type ShareAdapter = {
  out: (raw: string) => string | null
  in: (raw: string) => string | null | undefined
}

/** The share rule of every personal key on screen now, for the link builder. */
const sharers = new Map<string, Sharer>()
export const currentSharers = (): ReadonlyMap<string, Sharer> => sharers

/**
 * Like useState, but kept for this history entry under `key`: in the query string, or
 * — with `{ personal: true }`, for anything naming a person — in router state.
 * `fallback` is the value when the key is absent, and a value equal to it is removed.
 */
export function useUrlState<T>(
  key: string,
  fallback: T,
  codec: Codec<T>,
  opts: { personal?: boolean; share?: false | ShareAdapter } = {},
): [T, (next: T | ((prev: T) => T)) => void] {
  const location = useLocation()
  const navigate = useNavigate()
  const personal = opts.personal === true

  // The share rule, registered while this page is on screen. The adapter is a fresh
  // closure each render (it reads the page's rows), so the registry holds a stable
  // wrapper that calls the latest.
  const adapter = useRef<ShareAdapter | null>(null)
  adapter.current = opts.share ? opts.share : null
  const excluded = opts.share === false
  const adapted = Boolean(opts.share)
  useEffect(() => {
    if (!personal || (!excluded && !adapted)) return
    const s: Sharer = excluded
      ? { kind: 'exclude' }
      : { kind: 'adapt', out: (raw) => adapter.current?.out(raw) ?? null }
    sharers.set(key, s)
    return () => {
      if (sharers.get(key) === s) sharers.delete(key)
    }
  }, [key, personal, excluded, adapted])

  // A shared link's adapted value, waiting for this page to turn it back into indexes.
  const incoming = personal
    ? ((location.state as Record<string, Record<string, string> | undefined> | null)?.[INCOMING_KEY]?.[key] ?? null)
    : null
  const pendingIn = incoming !== null ? adapter.current?.in(incoming) : undefined
  useEffect(() => {
    if (incoming === null || pendingIn === undefined) return
    const state = liveState()
    const waiting = { ...((state[INCOMING_KEY] as Record<string, string> | undefined) ?? {}) }
    if (waiting[key] !== incoming) return
    delete waiting[key]
    const view = { ...(state[VIEW] ?? {}) }
    if (pendingIn === null) delete view[key]
    else view[key] = pendingIn
    state[VIEW] = view
    if (Object.keys(waiting).length) state[INCOMING_KEY] = waiting
    else delete state[INCOMING_KEY]
    navigate(
      { pathname: window.location.pathname, search: window.location.search, hash: window.location.hash },
      { replace: true, state },
    )
  }, [incoming, pendingIn, key, navigate])

  const raw = personal
    ? ((location.state as RouterState | null)?.[VIEW]?.[key] ?? null)
    : new URLSearchParams(location.search).get(key)

  // Keyed on the raw string, so a list parses to the SAME array until it actually
  // changes — a fresh array every render would rerun every memo below it.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const value = useMemo(() => (raw === null ? fallback : codec.parse(raw)), [raw])

  const set = useCallback(
    (next: T | ((prev: T) => T)) => {
      // Both homes, live: an earlier write in this same click has already replaced them.
      const params = new URLSearchParams(window.location.search)
      const state = liveState()
      const view = { ...(state[VIEW] ?? {}) }

      const cur = personal ? (view[key] ?? null) : params.get(key)
      const prev = cur === null ? fallback : codec.parse(cur)
      const v = typeof next === 'function' ? (next as (p: T) => T)(prev) : next
      const out = codec.format(v)
      const drop = out === null || out === codec.format(fallback)
      if (drop && cur === null) return
      if (!drop && out === cur) return

      if (personal) {
        if (drop) delete view[key]
        else view[key] = out
        state[VIEW] = view
      } else if (drop) params.delete(key)
      else params.set(key, out)

      const qs = params.toString()
      navigate(
        { pathname: window.location.pathname, search: qs ? `?${qs}` : '', hash: window.location.hash },
        { replace: true, state },
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [key, navigate, personal],
  )

  return [value, set]
}
